import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MenuItemWithTranslations } from '../types/menu';
import { pizzaCategories } from '../data/menuData';
import { ChevronRight, ChevronDown } from 'lucide-react';
import MenuCategory from './MenuCategory';
import { motion, AnimatePresence } from 'framer-motion';

interface PizzaMenuProps {
  items: MenuItemWithTranslations[];
}

const PizzaMenu: React.FC<PizzaMenuProps> = ({ items }) => {
  const { t } = useLanguage();
  // Auto-open the first subcategory so pizza items are visible right away
  // (and so category clicks can scroll to a real item instead of an
  // empty, collapsed list).
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>('speciales');

  const pizzasBySubcategory = items.reduce((acc: { [key: string]: MenuItemWithTranslations[] }, item) => {
    if (item.subcategory) {
      if (!acc[item.subcategory]) {
        acc[item.subcategory] = [];
      }
      acc[item.subcategory].push(item);
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(pizzaCategories).map(([key]) => {
        const categoryItems = pizzasBySubcategory[key] || [];
        return (
          <div key={key} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setSelectedSubcategory(selectedSubcategory === key ? null : key)}
              aria-expanded={selectedSubcategory === key}
              aria-controls={`pizza-subcategory-${key}`}
              className="w-full px-6 py-4 flex justify-between items-center bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] hover:to-[#FDE68A] transition-colors"
            >
              <span className="text-lg font-semibold text-stone-800">
                {t(`menu.categories.pizza.${key}`)}
              </span>
              {selectedSubcategory === key ? (
                <ChevronDown className="w-5 h-5 text-[#D97706]" aria-hidden="true" />
              ) : (
                <ChevronRight className="w-5 h-5 text-[#D97706]" aria-hidden="true" />
              )}
            </button>
            
            <AnimatePresence>
              {selectedSubcategory === key && (
                <motion.div
                  id={`pizza-subcategory-${key}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {categoryItems.length > 0 ? (
                    <div className="p-4">
                      <MenuCategory items={categoryItems} />
                    </div>
                  ) : (
                    <p className="p-4 text-sm text-gray-500">No items in this subcategory yet.</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default PizzaMenu; 